import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";

import { api, errMsg } from "../lib/api";
import { useAuth } from "../stores/auth";
import { usePrefs } from "../stores/prefs";
import { beep, vibrate } from "../lib/sound";

import { TopBar } from "../components/ui/TopBar";
import { Scanner } from "../components/ui/Scanner";
import { QtyStepper } from "../components/ui/QtyStepper";
import { Field } from "../components/ui/Field";
import { Icon } from "../components/ui/Icon";
import { Button } from "../components/ui/Button";
import { Banner } from "../components/ui/Banner";
import { Stepper } from "../components/ui/Stepper";
import { SectionTitle } from "../components/ui/SectionTitle";
import { ProductThumb, inferCategory } from "../components/ui/ProductThumb";
import { ProductSearch } from "../components/ui/ProductSearch";

type Location = {
  code: string;
  name: string;
  city: string | null;
  type: string;
  active: boolean;
};

type Line = {
  sku: string;
  qty: number;
  name?: string | null;
  image_url?: string | null;
};

const todayCode = (): string => {
  const d = new Date();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${d.getFullYear()}${m}${day}`;
};

export function TransferOut() {
  const nav = useNavigate();
  const user = useAuth((s) => s.user);
  const prefs = usePrefs((s) => s.prefs);

  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [selectedLocations, setSelectedLocations] = useState<string[]>([]);
  const [qrBag, setQrBag] = useState("");
  const [lines, setLines] = useState<Line[]>([]);
  const [skuInput, setSkuInput] = useState("");
  const [qtyInput, setQtyInput] = useState(1);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const { data: locations } = useQuery<Location[]>({
    queryKey: ["locations"],
    queryFn: async () => (await api.get("/api/locations")).data,
  });
  const vendingLocs = useMemo(
    () => (locations ?? []).filter((l) => l.type === "vending" && l.active),
    [locations],
  );
  const selectedLocNames = useMemo(
    () =>
      selectedLocations
        .map((c) => vendingLocs.find((l) => l.code === c)?.name || c)
        .filter(Boolean),
    [selectedLocations, vendingLocs],
  );

  const operatorName = user?.display_name?.split(" ")[0] || user?.username || "—";
  const operatorInitials = (() => {
    const src = (user?.display_name || user?.username || "FB").trim();
    const parts = src.split(/\s+/);
    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  })();

  const trpCode = useMemo(() => {
    const init = operatorInitials.padEnd(2, "X");
    return `TRP-${todayCode()}-${init}`;
  }, [operatorInitials]);

  const totalItems = useMemo(() => lines.reduce((s, l) => s + l.qty, 0), [lines]);

  function toggleLoc(code: string) {
    setSelectedLocations((s) => (s.includes(code) ? s.filter((c) => c !== code) : [...s, code]));
  }

  function addLine() {
    if (!skuInput || qtyInput <= 0) return;
    const sku = skuInput.toUpperCase().trim();
    let isNew = false;
    setLines((ls) => {
      const idx = ls.findIndex((l) => l.sku === sku);
      if (idx >= 0) {
        const updated = [...ls];
        updated[idx] = { ...updated[idx], qty: updated[idx].qty + qtyInput };
        return updated;
      }
      isNew = true;
      return [...ls, { sku, qty: qtyInput }];
    });
    setSkuInput("");
    setQtyInput(1);
    if (prefs.beep) beep("ok");
    if (prefs.vibrate) vibrate(30);
    if (isNew) {
      api
        .get(`/api/stock/${sku}`)
        .then(({ data }) =>
          setLines((ls) =>
            ls.map((l) =>
              l.sku === sku ? { ...l, name: data.name, image_url: data.image_url } : l,
            ),
          ),
        )
        .catch(() => {});
    }
  }

  function adjustLine(sku: string, delta: number) {
    setLines((ls) =>
      ls
        .map((l) => (l.sku === sku ? { ...l, qty: Math.max(0, l.qty + delta) } : l))
        .filter((l) => l.qty > 0),
    );
  }

  function removeLine(sku: string) {
    setLines((ls) => ls.filter((l) => l.sku !== sku));
  }

  async function submit() {
    if (lines.length === 0 || selectedLocations.length === 0 || busy) return;
    setBusy(true);
    setErr(null);
    try {
      const { data } = await api.post("/api/transfer/open", {
        operator: user?.username,
        expected_locations: selectedLocations,
        qr_bag_code: qrBag || null,
        lines: lines.map((l) => ({ sku: l.sku, qty_out: l.qty })),
      });
      if (prefs.beep) beep("ok");
      if (prefs.vibrate) vibrate([20, 30, 20]);
      nav(`/transfer-in?focus=${data.id}`, { replace: true });
    } catch (e) {
      if (prefs.beep) beep("error");
      setErr(errMsg(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <TopBar back onBack={() => nav(-1)} title="Transfer Out" sub="Apertura viaggio refill" />

      <Stepper total={3} step={step} />
      <div
        style={{
          padding: "6px 18px 0",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <div
          style={{
            fontSize: 10,
            fontWeight: 800,
            color: "var(--tcg-muted)",
            letterSpacing: "0.08em",
            textTransform: "uppercase",
          }}
        >
          STEP {step} / 3
        </div>
        <div style={{ fontSize: 11, fontWeight: 700, color: "var(--tcg-ink-soft)" }}>
          {step === 1 ? "Operatore" : step === 2 ? "Tappe & sacchetto" : "Carico SKU"}
        </div>
      </div>

      {/* Trip hero (visibile da step 2 in poi) */}
      {step >= 2 && (
        <div style={{ padding: "12px 18px 0" }}>
          <div
            style={{
              background: "var(--tcg-navy)",
              color: "var(--tcg-cream)",
              borderRadius: 18,
              padding: "14px 16px",
              position: "relative",
              overflow: "hidden",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                marginBottom: 10,
              }}
            >
              <div
                style={{
                  fontSize: 10,
                  color: "var(--tcg-yellow)",
                  fontWeight: 800,
                  letterSpacing: "0.08em",
                  textTransform: "uppercase",
                }}
              >
                ↗ Viaggio in apertura
              </div>
              <div
                className="tcg-mono"
                style={{ fontSize: 10, color: "rgba(255,255,255,0.6)", fontWeight: 700 }}
              >
                {trpCode}
              </div>
            </div>
            <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
              <div
                style={{
                  width: 38,
                  height: 38,
                  borderRadius: 11,
                  background: "var(--tcg-yellow)",
                  color: "var(--tcg-navy-deep)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: 12,
                  fontWeight: 900,
                }}
              >
                {operatorInitials}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 14, fontWeight: 800 }}>{operatorName}</div>
                <div
                  style={{
                    fontSize: 10,
                    color: "rgba(255,255,255,0.65)",
                    fontWeight: 600,
                    marginTop: 2,
                    display: "flex",
                    alignItems: "center",
                    gap: 4,
                  }}
                >
                  <Icon name="pin" size={10} />
                  {selectedLocations.length} location
                  {selectedLocNames.length > 0 && ` · ${selectedLocNames.join(" · ")}`}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {step === 1 && (
        <div style={{ padding: "16px 18px 0" }}>
          <div className="tcg-card" style={{ padding: 14 }}>
            <div
              style={{
                fontSize: 10,
                fontWeight: 800,
                color: "var(--tcg-muted)",
                letterSpacing: "0.08em",
                textTransform: "uppercase",
                marginBottom: 10,
              }}
            >
              Operatore del viaggio
            </div>
            <div
              style={{
                display: "flex",
                gap: 12,
                alignItems: "center",
                background: "var(--tcg-bg)",
                padding: 12,
                borderRadius: 12,
                border: "1.5px solid var(--tcg-navy)",
              }}
            >
              <div
                style={{
                  width: 44,
                  height: 44,
                  borderRadius: 12,
                  background: "var(--tcg-navy)",
                  color: "var(--tcg-yellow)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: 14,
                  fontWeight: 900,
                }}
              >
                {operatorInitials}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 14, fontWeight: 800 }}>{operatorName}</div>
                <div
                  className="tcg-mono"
                  style={{ fontSize: 10, color: "var(--tcg-muted)", marginTop: 2, fontWeight: 700 }}
                >
                  @{user?.username || "—"} · {user?.role || "operator"}
                </div>
              </div>
              <span className="tcg-badge tcg-badge-navy">TU</span>
            </div>
            <div className="tcg-helper" style={{ marginTop: 10 }}>
              Il viaggio sarà registrato a tuo nome. Le rettifiche di stock SEDE → viaggio sono atomiche.
            </div>
          </div>

          <div style={{ marginTop: 14 }}>
            <Button variant="yellow" block onClick={() => setStep(2)}>
              Avanti · scegli location <Icon name="chev-r" />
            </Button>
          </div>
        </div>
      )}

      {step === 2 && (
        <>
          <SectionTitle right={<span style={{ fontSize: 11, color: "var(--tcg-muted)", paddingRight: 18, fontWeight: 700 }}>{selectedLocations.length} selezionate</span>}>
            Seleziona tappe
          </SectionTitle>
          <div style={{ padding: "0 18px", display: "flex", flexWrap: "wrap", gap: 6 }}>
            {vendingLocs.length === 0 && (
              <div className="tcg-helper">Nessuna location vending attiva. Aggiungile da Settings.</div>
            )}
            {vendingLocs.map((l) => {
              const sel = selectedLocations.includes(l.code);
              return (
                <button
                  key={l.code}
                  type="button"
                  className={`tcg-chip-pill ${sel ? "on" : ""}`}
                  onClick={() => toggleLoc(l.code)}
                >
                  {sel && <Icon name="check" size={10} />}
                  {l.name}
                </button>
              );
            })}
          </div>

          <div style={{ padding: "20px 18px 0" }}>
            <Field
              label="QR sacchetto (opzionale)"
              hint="Codice del quaderno cartaceo: TRP-A-01, TRP-A-02…"
            >
              <input
                className="tcg-input tcg-mono"
                value={qrBag}
                onChange={(e) => setQrBag(e.target.value.toUpperCase().trim())}
                placeholder="TRP-A-01"
                autoCapitalize="characters"
              />
            </Field>
          </div>

          <div style={{ display: "flex", gap: 8, padding: "0 18px" }}>
            <Button variant="secondary" onClick={() => setStep(1)}>
              <Icon name="chev-l" /> Indietro
            </Button>
            <Button
              variant="yellow"
              style={{ flex: 1 }}
              onClick={() => setStep(3)}
              disabled={selectedLocations.length === 0}
            >
              Avanti · carica prodotti <Icon name="chev-r" />
            </Button>
          </div>
        </>
      )}

      {step === 3 && (
        <>
          <SectionTitle>Scansiona prodotti</SectionTitle>
          <div style={{ padding: "0 18px" }}>
            <Scanner
              onDetect={(c) => setSkuInput(c.toUpperCase().trim())}
              hint="Inquadra il barcode oppure inserisci SKU"
            />
          </div>

          <div style={{ padding: "12px 18px 0" }}>
            <ProductSearch
              selectedSku={skuInput}
              onPick={(p) => {
                setSkuInput(p.sku);
                if (prefs.vibrate) vibrate(20);
              }}
            />
          </div>

          <div style={{ padding: "12px 18px 0" }}>
            <div className="tcg-card" style={{ padding: 12 }}>
              <label className="tcg-label">SKU</label>
              <input
                className="tcg-input tcg-mono"
                value={skuInput}
                onChange={(e) => setSkuInput(e.target.value.toUpperCase().trim())}
                placeholder="POK-TER-151-IT-ETB"
                autoCapitalize="characters"
              />
              <label className="tcg-label" style={{ marginTop: 12 }}>
                Qtà
              </label>
              <QtyStepper value={qtyInput} onChange={setQtyInput} min={1} />
              <Button
                variant="primary"
                block
                disabled={!skuInput}
                onClick={addLine}
                style={{ marginTop: 12 }}
              >
                <Icon name="plus" size={14} /> Aggiungi al viaggio
              </Button>
            </div>
          </div>

          <SectionTitle
            right={
              <div style={{ paddingRight: 18, fontSize: 11, color: "var(--tcg-ink-soft)", fontWeight: 700 }}>
                <span style={{ color: "var(--tcg-muted)" }}>SKU </span>
                <span className="tcg-num">{lines.length}</span>
                <span style={{ color: "var(--tcg-muted)" }}> · pezzi </span>
                <span className="tcg-num">{totalItems}</span>
              </div>
            }
          >
            Carico viaggio
          </SectionTitle>
          <div
            style={{
              background: "var(--tcg-card)",
              marginInline: 18,
              borderRadius: 16,
              border: "1px solid var(--tcg-border)",
              overflow: "hidden",
            }}
          >
            {lines.length === 0 && (
              <div style={{ padding: 16, fontSize: 12, color: "var(--tcg-muted)", textAlign: "center" }}>
                Ancora nessun prodotto. Scansiona o inserisci uno SKU.
              </div>
            )}
            {lines.map((l, i, arr) => {
              return (
                <div
                  key={l.sku}
                  style={{
                    display: "flex",
                    gap: 10,
                    padding: 12,
                    alignItems: "center",
                    borderBottom:
                      i === arr.length - 1 ? "none" : "1px solid var(--tcg-border)",
                  }}
                >
                  <ProductThumb imageUrl={l.image_url} sku={l.sku} size="xs" radius={9} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div
                      style={{
                        fontSize: 12,
                        fontWeight: 700,
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {l.name ||
                        `${inferCategory(l.sku).label} · ${l.sku.split("-").slice(1).join("-") || l.sku}`}
                    </div>
                    <div
                      className="tcg-mono"
                      style={{ fontSize: 10, color: "var(--tcg-muted)", fontWeight: 600, marginTop: 1 }}
                    >
                      {l.sku}
                    </div>
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 4, flexShrink: 0 }}>
                    <button
                      type="button"
                      onClick={() => adjustLine(l.sku, -1)}
                      style={{
                        width: 28,
                        height: 28,
                        border: "1px solid var(--tcg-border)",
                        background: "var(--tcg-cream)",
                        borderRadius: 7,
                        color: "var(--tcg-muted)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        cursor: "pointer",
                      }}
                      aria-label="meno"
                    >
                      <Icon name="minus" size={12} />
                    </button>
                    <div className="tcg-num" style={{ width: 32, textAlign: "center", fontSize: 14, fontWeight: 800 }}>
                      {l.qty}
                    </div>
                    <button
                      type="button"
                      onClick={() => adjustLine(l.sku, 1)}
                      style={{
                        width: 28,
                        height: 28,
                        border: "1px solid var(--tcg-border)",
                        background: "var(--tcg-cream)",
                        borderRadius: 7,
                        color: "var(--tcg-navy)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        cursor: "pointer",
                      }}
                      aria-label="più"
                    >
                      <Icon name="plus" size={12} />
                    </button>
                    <button
                      type="button"
                      onClick={() => removeLine(l.sku)}
                      style={{
                        marginLeft: 6,
                        background: "transparent",
                        border: "none",
                        color: "var(--tcg-danger)",
                        fontSize: 11,
                        fontWeight: 700,
                        cursor: "pointer",
                      }}
                      aria-label="rimuovi"
                    >
                      <Icon name="trash" size={12} />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>

          {err && (
            <div style={{ padding: "12px 18px 0" }}>
              <Banner tone="danger" icon="x">
                {err}
              </Banner>
            </div>
          )}

          <div style={{ display: "flex", gap: 8, padding: "16px 18px 0" }}>
            <Button variant="secondary" onClick={() => setStep(2)} disabled={busy}>
              <Icon name="chev-l" />
            </Button>
            <Button
              variant="yellow"
              style={{ flex: 1 }}
              disabled={busy || lines.length === 0 || selectedLocations.length === 0}
              onClick={submit}
            >
              <Icon name="truck" size={18} />
              {busy ? "Apertura…" : `Apri viaggio · ${totalItems} pezzi`}
            </Button>
          </div>
        </>
      )}

      <div style={{ height: 24 }} />
    </>
  );
}
